import React, { useCallback, useEffect, useId, useState } from "react";
import { Group, Player } from "../api/client";
import { paymentScheduleApi as api, PaymentStatus, ScheduleEntry } from "../api/paymentSchedule";
import { useConfirm } from "../components/ConfirmDialog";
import PageLoader from "../components/PageLoader";
import "./PaymentSchedulePage.css";

const MONTHS = ["янв", "фев", "мар", "апр", "май", "июн", "июл", "авг", "сен", "окт", "ноя", "дек"];
const MONTH_NUMBERS = MONTHS.map((_, i) => i + 1);
const NO_GROUP = "none";

type Kind = "none" | "planned" | "invoiced" | "processing" | "paid" | "cancelled" | "unpaid" | "refunded";

const SHORT_LABEL: Record<Exclude<Kind, "none">, string> = {
  planned: "В плане",
  invoiced: "Счёт выставлен",
  processing: "В обработке",
  paid: "Оплачен",
  cancelled: "Отозван",
  unpaid: "Не оплачен",
  refunded: "Возврат",
};

const LEGEND: { kind: Exclude<Kind, "none">; hint: string }[] = [
  { kind: "planned", hint: "счёт выставится автоматически" },
  { kind: "invoiced", hint: "ждёт оплаты" },
  { kind: "processing", hint: "оплата проходит" },
  { kind: "paid", hint: "картой или наличными" },
  { kind: "cancelled", hint: "счёт отозван" },
  { kind: "unpaid", hint: "ошибка оплаты или истёк срок" },
  { kind: "refunded", hint: "деньги вернули" },
];

const STATUS_LABEL: Record<PaymentStatus, string> = {
  pending: "Счёт выставлен, ждёт оплаты",
  processing: "Оплата в обработке",
  completed: "Оплачен",
  manual: "Оплачен наличными",
  failed: "Ошибка оплаты",
  expired: "Срок оплаты истёк",
  cancelled: "Счёт отозван",
  refunded: "Деньги возвращены",
};

function kindOf(entry: ScheduleEntry | undefined): Kind {
  if (!entry) return "none";
  if (!entry.payment_status) return "planned";
  switch (entry.payment_status) {
    case "pending":
      return "invoiced";
    case "processing":
      return "processing";
    case "completed":
    case "manual":
      return "paid";
    case "cancelled":
      return "cancelled";
    case "refunded":
      return "refunded";
    default:
      return "unpaid";
  }
}

// Mirrors the server's refusal to remove a month: an invoice in flight or paid.
function isLocked(entry: ScheduleEntry | undefined): boolean {
  const s = entry?.payment_status;
  return s === "pending" || s === "processing" || s === "completed" || s === "manual";
}

const rubles = new Intl.NumberFormat("ru-RU", {
  style: "currency",
  currency: "RUB",
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});
const money = (value: number) => rubles.format(value);

const monthLabel = (month: number, year: number) => `${MONTHS[month - 1]} ${year}`;

function plural(n: number, forms: [string, string, string]): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return forms[0];
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return forms[1];
  return forms[2];
}

const cells = (n: number) => `${n} ${plural(n, ["ячейку", "ячейки", "ячеек"])}`;

function parseAmount(draft: string): number | null | "invalid" {
  const text = draft.trim().replace(",", ".");
  if (text === "") return null;
  const value = Number(text);
  return Number.isFinite(value) && value > 0 ? Math.round(value * 100) / 100 : "invalid";
}

const keyOf = (playerID: string, month: number) => `${playerID}-${month}`;
const byName = <T extends { name: string }>(a: T, b: T) => a.name.localeCompare(b.name, "ru");

type Selected = { playerID: string; month: number };

export default function PaymentSchedulePage() {
  const thisYear = new Date().getFullYear();
  const [year, setYear] = useState(thisYear);
  const [groups, setGroups] = useState<Group[]>([]);
  const [players, setPlayers] = useState<Player[]>([]);
  const [entries, setEntries] = useState<Map<string, ScheduleEntry>>(new Map());
  const [groupFees, setGroupFees] = useState<Map<string, number>>(new Map());
  const [baseFee, setBaseFee] = useState<number | null>(null);
  const [groupID, setGroupID] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState<Selected | null>(null);
  const [amountDraft, setAmountDraft] = useState("");
  const [note, setNote] = useState("");
  const [bulkMonths, setBulkMonths] = useState<Set<number>>(new Set());
  const [bulkExcluded, setBulkExcluded] = useState<Set<string>>(new Set());
  const [bulkAmount, setBulkAmount] = useState("");
  const { confirm, dialog } = useConfirm();
  const ids = { amount: useId(), note: useId(), bulkAmount: useId() };

  const load = useCallback(async () => {
    try {
      const [groupList, playerList, schedule, fees, base] = await Promise.all([
        api.listGroups(),
        api.listPlayers(),
        api.listSchedule(year),
        // Fees only label cells without an own amount; the grid works without them.
        api.listGroupFees().catch((err) => {
          console.error("Failed to load group fees", err);
          return null;
        }),
        api.getBaseFee().catch((err) => {
          console.error("Failed to load base fee", err);
          return null;
        }),
      ]);
      const sortedGroups = (groupList ?? []).slice().sort(byName);
      const active = (playerList ?? []).filter((p) => p.is_active).sort(byName);
      setGroups(sortedGroups);
      setPlayers(active);
      setEntries(new Map((schedule.entries ?? []).map((e) => [keyOf(e.player_id, e.month), e])));
      setGroupFees(
        new Map(
          (fees ?? [])
            .filter((f) => f.has_override && typeof f.monthly_fee === "number")
            .map((f) => [f.group_id, f.monthly_fee as number])
        )
      );
      setBaseFee(base?.current?.monthly_fee ?? null);
      setGroupID(
        (current) =>
          current ||
          sortedGroups[0]?.id ||
          (active.some((p) => !p.group_id) ? NO_GROUP : "")
      );
    } catch (err: any) {
      console.error("Failed to load payment schedule", err);
      setError(err?.message || "Не удалось загрузить график счетов");
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => {
    load();
  }, [load]);

  const rows = players.filter((p) => (groupID === NO_GROUP ? !p.group_id : p.group_id === groupID));
  const hasPlayersWithoutGroup = players.some((p) => !p.group_id);

  const fallbackFee = (p: Player): { amount: number; source: string } | null => {
    const groupFee = p.group_id ? groupFees.get(p.group_id) : undefined;
    if (groupFee !== undefined) return { amount: groupFee, source: "ставке группы" };
    if (baseFee !== null) return { amount: baseFee, source: "базовой ставке" };
    return null;
  };

  const run = async (action: () => Promise<unknown>, failure: string): Promise<boolean> => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await action();
      return true;
    } catch (err: any) {
      console.error("Payment schedule action failed", err);
      setError(err?.message || failure);
      return false;
    } finally {
      setBusy(false);
      await load();
    }
  };

  const selectCell = (playerID: string, month: number) => {
    const entry = entries.get(keyOf(playerID, month));
    setSelected({ playerID, month });
    setAmountDraft(entry?.amount != null ? String(entry.amount) : "");
    setNote("");
  };

  const changeGroup = (value: string) => {
    setGroupID(value);
    setSelected(null);
    setBulkExcluded(new Set());
  };

  const changeYear = (value: number) => {
    setYear(value);
    setSelected(null);
  };

  const selectedPlayer = selected ? players.find((p) => p.id === selected.playerID) : undefined;
  const selectedEntry = selected ? entries.get(keyOf(selected.playerID, selected.month)) : undefined;

  const planSelected = async () => {
    if (!selected) return;
    const amount = parseAmount(amountDraft);
    if (amount === "invalid") return;
    await run(
      () => api.planMonth({ playerID: selected.playerID, year, month: selected.month }, amount ?? undefined),
      "Не удалось сохранить месяц в плане"
    );
  };

  const unplanSelected = async () => {
    if (!selected || !selectedPlayer) return;
    const label = monthLabel(selected.month, year);
    const confirmed = await confirm({
      title: `Убрать ${label} из плана?`,
      body: (
        <>
          <p>
            Счёт ребёнку «{selectedPlayer.name}» за {label} не выставится автоматически.
          </p>
          {selectedEntry?.payment_status && <p>Прежний счёт за этот месяц останется в истории.</p>}
        </>
      ),
      confirmLabel: "Убрать из плана",
      danger: true,
    });
    if (!confirmed) return;
    await run(
      () => api.unplanMonth({ playerID: selected.playerID, year, month: selected.month }),
      "Не удалось убрать месяц из плана"
    );
  };

  const issueSelected = async () => {
    if (!selected || !selectedPlayer) return;
    const label = monthLabel(selected.month, year);
    const saved = selectedEntry?.amount ?? fallbackFee(selectedPlayer)?.amount ?? null;
    const draft = parseAmount(amountDraft);
    const unsavedDraft = draft !== "invalid" && draft !== (selectedEntry?.amount ?? null);
    const trimmedNote = note.trim();
    const due = `10.${String(selected.month).padStart(2, "0")}.${year}`;
    const confirmed = await confirm({
      title: "Выставить счёт?",
      body: (
        <>
          <p>
            Родители ребёнка «{selectedPlayer.name}» получат счёт за {label}
            {saved !== null ? ` на ${money(saved)}` : ""} и уведомление в приложении. Оплатить его
            нужно до {due}.
          </p>
          {!selectedEntry && <p>Месяц добавится в план.</p>}
          {selectedEntry?.payment_status && <p>Прежний счёт за этот месяц останется в истории.</p>}
          {unsavedDraft && <p>Несохранённая сумма из поля в счёт не попадёт.</p>}
          {trimmedNote && <p>Комментарий: «{trimmedNote}».</p>}
        </>
      ),
      confirmLabel: "Выставить счёт",
    });
    if (!confirmed) return;
    const issued = await run(
      () => api.issueInvoice({ playerID: selected.playerID, year, month: selected.month }, trimmedNote || undefined),
      "Не удалось выставить счёт"
    );
    if (issued) {
      setNote("");
      setNotice(`Счёт за ${label} выставлен.`);
    }
  };

  const bulkPlayers = rows.filter((p) => !bulkExcluded.has(p.id));
  const bulkMonthList = MONTH_NUMBERS.filter((m) => bulkMonths.has(m));
  const bulkAmountValue = parseAmount(bulkAmount);
  const bulkReady = !busy && bulkPlayers.length > 0 && bulkMonthList.length > 0 && bulkAmountValue !== "invalid";

  const toggleIn = <T,>(set: Set<T>, value: T): Set<T> => {
    const next = new Set(set);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    return next;
  };

  // One call per month with exactly the players whose cell changes, so the
  // server never touches a cell the admin wasn't told about.
  const applyBulk = async (action: "enable" | "disable", byMonth: Map<number, string[]>, amount?: number) => {
    let changed = 0;
    let failed = 0;
    const done = await run(async () => {
      for (const [month, playerIDs] of Array.from(byMonth)) {
        const result = await api.bulk({ action, year, playerIDs, months: [month], amount });
        changed += result.applied;
        failed += result.failed;
      }
    }, "Не удалось изменить план");
    if (!done) return;
    if (failed > 0) setError(`Не удалось изменить ячеек: ${failed}. Остальные изменены.`);
    else setNotice(`Готово, ячеек изменено: ${changed}.`);
  };

  const bulkEnable = async () => {
    if (!bulkReady) return;
    const amount = bulkAmountValue ?? undefined;
    const byMonth = new Map<number, string[]>();
    let added = 0;
    let repriced = 0;
    let reopened = 0;
    let locked = 0;
    let same = 0;
    for (const month of bulkMonthList) {
      for (const p of bulkPlayers) {
        const entry = entries.get(keyOf(p.id, month));
        if (isLocked(entry)) {
          locked++;
          continue;
        }
        if (!entry) added++;
        else if (amount !== undefined && entry.amount !== amount) {
          repriced++;
          if (entry.payment_status === "cancelled") reopened++;
        } else {
          same++;
          continue;
        }
        byMonth.set(month, [...(byMonth.get(month) ?? []), p.id]);
      }
    }
    const total = added + repriced;
    if (total === 0) {
      setError(null);
      setNotice("Менять нечего: выбранные месяцы уже в плане с этой суммой или по ним есть счёт.");
      return;
    }
    const confirmed = await confirm({
      title: `Изменить ${cells(total)}?`,
      body: (
        <>
          {added > 0 && (
            <p>
              В план добавится: {added}
              {amount !== undefined ? `, с суммой ${money(amount)}` : ", по ставке группы"}.
            </p>
          )}
          {repriced > 0 && amount !== undefined && (
            <p>
              Сумма станет {money(amount)}: {repriced}.
            </p>
          )}
          {reopened > 0 && (
            <p>Из них с отозванным счётом: {reopened} — автомат снова начнёт выставлять по ним счета.</p>
          )}
          {locked > 0 && <p>Месяцы со счётом не меняются: {locked}.</p>}
          {same > 0 && <p>Без изменений: {same}.</p>}
        </>
      ),
      confirmLabel: "Добавить в план",
    });
    if (!confirmed) return;
    await applyBulk("enable", byMonth, amount);
  };

  const bulkDisable = async () => {
    if (!bulkReady) return;
    const byMonth = new Map<number, string[]>();
    let removed = 0;
    let locked = 0;
    for (const month of bulkMonthList) {
      for (const p of bulkPlayers) {
        const entry = entries.get(keyOf(p.id, month));
        if (!entry) continue;
        if (isLocked(entry)) {
          locked++;
          continue;
        }
        removed++;
        byMonth.set(month, [...(byMonth.get(month) ?? []), p.id]);
      }
    }
    if (removed === 0) {
      setError(null);
      setNotice("Менять нечего: в выбранных ячейках нет месяцев в плане без счёта.");
      return;
    }
    const confirmed = await confirm({
      title: `Убрать из плана ${cells(removed)}?`,
      body: (
        <>
          <p>По этим месяцам счета не выставятся автоматически. Прежние счета останутся в истории.</p>
          {locked > 0 && <p>Месяцы со счётом не меняются: {locked}.</p>}
        </>
      ),
      confirmLabel: "Убрать из плана",
      danger: true,
    });
    if (!confirmed) return;
    await applyBulk("disable", byMonth);
  };

  if (loading) return <PageLoader />;

  const yearOptions = [thisYear - 1, thisYear, thisYear + 1].filter((y) => y >= 2020 && y <= 2100);
  if (!yearOptions.includes(year)) yearOptions.push(year);

  const renderPanel = () => {
    if (!selected || !selectedPlayer) return null;
    const label = monthLabel(selected.month, year);
    const kind = kindOf(selectedEntry);
    const locked = isLocked(selectedEntry);
    const draft = parseAmount(amountDraft);
    const fallback = fallbackFee(selectedPlayer);
    const status = selectedEntry?.payment_status;
    const canSaveAmount =
      !busy && typeof draft === "number" && draft !== (selectedEntry?.amount ?? null);

    let amountHint: string;
    if (selectedEntry?.amount != null) amountHint = "Своя сумма заменяет ставку для этого месяца.";
    else if (fallback) amountHint = `${selectedEntry ? "Сейчас" : "Пустое поле"} — по ${fallback.source}: ${money(fallback.amount)}.`;
    else amountHint = "Ставка группы не задана — укажите свою сумму.";

    return (
      <section className="mav-card ps-panel" aria-label={`${selectedPlayer.name}, ${label}`}>
        <div className="ps-panel-head">
          <h3>
            {selectedPlayer.name}, {label}
          </h3>
          <span className={`ps-badge ps-kind-${kind}`}>{status ? STATUS_LABEL[status] : kind === "planned" ? "В плане, счёта ещё нет" : "Не в плане"}</span>
        </div>

        {status && selectedEntry?.payment_amount != null && (
          <p className="ps-panel-line">Сумма счёта: {money(selectedEntry.payment_amount)}</p>
        )}

        {locked ? (
          <p className="mav-hint">
            {kind === "paid" ? "Месяц оплачен" : "Счёт уже выставлен"}, поэтому месяц нельзя убрать из
            плана и поменять его сумму.
          </p>
        ) : (
          <>
            <div className="ps-panel-row">
              <div className="cp-field ps-amount-field">
                <label htmlFor={ids.amount}>Своя сумма, ₽</label>
                <input
                  id={ids.amount}
                  className={draft === "invalid" ? "mav-input has-error" : "mav-input"}
                  type="number"
                  min="1"
                  step="any"
                  inputMode="decimal"
                  value={amountDraft}
                  disabled={busy}
                  onChange={(e) => setAmountDraft(e.target.value)}
                />
                <span className="mav-hint">{amountHint}</span>
              </div>
              <div className="row-actions ps-panel-actions">
                {selectedEntry ? (
                  <button type="button" className="mav-btn-secondary" disabled={!canSaveAmount} onClick={planSelected}>
                    Сохранить сумму
                  </button>
                ) : (
                  <button
                    type="button"
                    className="mav-btn-primary"
                    disabled={busy || draft === "invalid"}
                    onClick={planSelected}
                  >
                    Добавить в план
                  </button>
                )}
                {selectedEntry && (
                  <button type="button" className="btn-quiet-danger" disabled={busy} onClick={unplanSelected}>
                    Убрать из плана
                  </button>
                )}
              </div>
            </div>
            {status === "cancelled" && (
              <p className="mav-hint">
                Счёт за этот месяц отозван. Если сохранить сумму, автомат снова начнёт выставлять его по
                плану.
              </p>
            )}

            {kind !== "refunded" && (
              <div className="ps-panel-row ps-issue">
                <div className="cp-field ps-note-field">
                  <label htmlFor={ids.note}>Комментарий для родителей</label>
                  <textarea
                    id={ids.note}
                    className="mav-input ps-note"
                    rows={2}
                    maxLength={500}
                    value={note}
                    disabled={busy}
                    onChange={(e) => setNote(e.target.value)}
                  />
                  <span className="mav-hint">Необязательно. Родители увидят его в счёте.</span>
                </div>
                <div className="row-actions ps-panel-actions">
                  <button type="button" className="mav-btn-primary" disabled={busy} onClick={issueSelected}>
                    Выставить счёт сейчас
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        <div className="ps-panel-close">
          <button type="button" className="mav-btn-secondary" onClick={() => setSelected(null)}>
            Закрыть
          </button>
        </div>
      </section>
    );
  };

  return (
    <div className="page page-wide ps-page">
      <h2>График счетов</h2>
      <p className="page-description">
        План ежемесячных счетов за тренировки. По месяцам в плане сервер сам выставляет счёт родителям,
        а сумму берёт из ячейки, ставки группы или базовой ставки. Оплаты здесь не проводятся: панель
        только планирует и выставляет счета.
      </p>

      {error && (
        <div className="error" role="alert">
          {error}
        </div>
      )}
      {notice && (
        <p className="ps-notice" role="status">
          {notice}
        </p>
      )}

      <div className="ps-filters">
        <label className="cp-field">
          <span>Группа</span>
          <select className="mav-input" value={groupID} onChange={(e) => changeGroup(e.target.value)}>
            {groups.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}
              </option>
            ))}
            {hasPlayersWithoutGroup && <option value={NO_GROUP}>Без группы</option>}
          </select>
        </label>
        <label className="cp-field">
          <span>Год</span>
          <select className="mav-input" value={year} onChange={(e) => changeYear(Number(e.target.value))}>
            {yearOptions.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </label>
      </div>

      <ul className="ps-legend" aria-label="Обозначения">
        {LEGEND.map((item) => (
          <li key={item.kind}>
            <span className={`ps-swatch ps-kind-${item.kind}`} aria-hidden="true" />
            <span className="ps-legend-label">{SHORT_LABEL[item.kind]}</span>
            <span className="ps-legend-hint">— {item.hint}</span>
          </li>
        ))}
      </ul>

      <div className="table-wrap ps-grid-wrap">
        <table className="ps-grid">
          <thead>
            <tr>
              <th scope="col" className="ps-name-col">
                Ребёнок
              </th>
              {MONTH_NUMBERS.map((m) => (
                <th key={m} scope="col">
                  {monthLabel(m, year)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={13} className="empty-cell">
                  {error ? "—" : "В группе нет активных детей"}
                </td>
              </tr>
            ) : (
              rows.map((p) => {
                const fallback = fallbackFee(p);
                return (
                  <tr key={p.id}>
                    <th scope="row" className="ps-name-col">
                      {p.name}
                    </th>
                    {MONTH_NUMBERS.map((m) => {
                      const entry = entries.get(keyOf(p.id, m));
                      const kind = kindOf(entry);
                      const own = entry?.payment_status ? entry.payment_amount ?? entry.amount : entry?.amount;
                      const shown = entry ? own ?? fallback?.amount : undefined;
                      const isFallback = entry !== undefined && own == null && shown !== undefined;
                      const state = kind === "none" ? "не в плане" : SHORT_LABEL[kind];
                      const isSelected = selected?.playerID === p.id && selected.month === m;
                      return (
                        <td key={m} className="ps-cell-td">
                          <button
                            type="button"
                            className={`ps-cell ps-kind-${kind}${isSelected ? " is-selected" : ""}`}
                            aria-pressed={isSelected}
                            aria-label={`${p.name}, ${monthLabel(m, year)}: ${state}${
                              shown !== undefined ? `, ${money(shown)}${isFallback ? ` по ${fallback?.source}` : ""}` : ""
                            }`}
                            onClick={() => selectCell(p.id, m)}
                          >
                            {kind === "none" ? (
                              <span className="ps-cell-state">—</span>
                            ) : (
                              <>
                                <span className="ps-cell-state">{SHORT_LABEL[kind]}</span>
                                {shown !== undefined && (
                                  <span className={isFallback ? "ps-cell-amount is-fallback" : "ps-cell-amount"}>
                                    {money(shown)}
                                  </span>
                                )}
                              </>
                            )}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {renderPanel()}

      <form
        className="mav-card ps-bulk"
        aria-label="Несколько месяцев сразу"
        onSubmit={(e) => e.preventDefault()}
      >
        <h3>Несколько месяцев сразу</h3>
        <p className="mav-hint">
          Отметьте месяцы и детей. Месяцы, по которым уже выставлен или оплачен счёт, не меняются.
        </p>
        <fieldset className="ps-fieldset">
          <legend>Месяцы</legend>
          <div className="ps-checks">
            {MONTH_NUMBERS.map((m) => (
              <label key={m} className="ps-check">
                <input
                  type="checkbox"
                  checked={bulkMonths.has(m)}
                  disabled={busy}
                  onChange={() => setBulkMonths((s) => toggleIn(s, m))}
                />
                <span>{monthLabel(m, year)}</span>
              </label>
            ))}
          </div>
        </fieldset>
        <fieldset className="ps-fieldset">
          <legend>Дети</legend>
          {rows.length === 0 ? (
            <p className="mav-hint">В группе нет активных детей</p>
          ) : (
            <div className="ps-checks">
              {rows.map((p) => (
                <label key={p.id} className="ps-check">
                  <input
                    type="checkbox"
                    checked={!bulkExcluded.has(p.id)}
                    disabled={busy}
                    onChange={() => setBulkExcluded((s) => toggleIn(s, p.id))}
                  />
                  <span>{p.name}</span>
                </label>
              ))}
            </div>
          )}
        </fieldset>
        <div className="ps-panel-row">
          <div className="cp-field ps-amount-field">
            <label htmlFor={ids.bulkAmount}>Своя сумма, ₽</label>
            <input
              id={ids.bulkAmount}
              className={bulkAmountValue === "invalid" ? "mav-input has-error" : "mav-input"}
              type="number"
              min="1"
              step="any"
              inputMode="decimal"
              value={bulkAmount}
              disabled={busy}
              onChange={(e) => setBulkAmount(e.target.value)}
            />
            <span className="mav-hint">Необязательно. Пустое поле — по ставке группы.</span>
          </div>
          <div className="row-actions ps-panel-actions">
            <button type="button" className="mav-btn-primary" disabled={!bulkReady} onClick={bulkEnable}>
              Добавить в план
            </button>
            <button type="button" className="btn-quiet-danger" disabled={!bulkReady} onClick={bulkDisable}>
              Убрать из плана
            </button>
          </div>
        </div>
      </form>

      {dialog}
    </div>
  );
}
